import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { PayoutInvoiceApi } from "../../../../../../../../services/Api/types";
import UpdatePartnerPayoutInvoiceModalForm from "./components/UpdatePartnerPayoutInvoiceModalForm";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "PARTNER_PAYOUT_INVOICE";
const resourceType = "PARTNER_PAYOUT_INVOICE";

const List = ({ resource }: ListState) => {
	const { t } = useTranslation("admin");
	const { addSuccessFlash } = useFlash();
	const [items, setItems] = useState<PayoutInvoiceApi[]>([]);
	const [params, setParams] = useState<Record<string, any>>({});
	const confirmation = useConfirmation();
	const [showUpdatePayoutInvoiceModal, setShowUpdatePayoutInvoiceModal] = useState<string | null>(null);
	const [payoutToShow, setPayoutToShow] = useState<PayoutInvoiceApi>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;

	if (!resource) return null;
	const data = resource.read();

	const fetchPayoutInvoices = async () => {
		if (config.fetch) {
			const res = await config.fetch(params);
			setItems(res);
		}
	};

	const onConfirmPayoutInvoice = async (id: string) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.confirmPartnerInvoice(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onRejectPayoutInvoice = async (id: string) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.rejectPartnerInvoice(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onPayPayoutInvoice = async (id: string) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.payForPartnerInvoice(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onCancelConfirmForPayoutInvoice = async (id: string) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.cancelConfirmForPartnerInvoice(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onCancelPaymentForPayoutInvoice = async (id: string) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		await adminApi.cancelPaymentForPartnerInvoice(id);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	let config = {
		fields: [
			{
				id: "partner",
				name: t("common.word.partner"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminPartnersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
				render: (item: PayoutInvoiceApi) => (
					<Link target={"_blank"} to={`/partnership/${item?.partner.id}`}>
						{item?.partner?.name}
					</Link>
				),
				renderExport: (item: PayoutInvoiceApi) => item.partner.name,
			},
			{
				id: "attachment",
				disableSorting: true,
				name: t("modules.partner_payout_invoice.field.attachments.title"),
				render: (item: PayoutInvoiceApi) => (
					<a
						rel="noreferrer"
						href={`${item.url}?access_token=${localStorage.getItem("token")}`}
						target="_blank"
					>
						{item.name}
					</a>
				),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: PayoutInvoiceApi) => `${FormatDate(item.created_at)}`,
			},
			{
				id: "issued_at",
				name: t("modules.partner_payout_invoice.field.issued_at.title"),
				type: "date",
				render: (item: PayoutInvoiceApi) => `${FormatDate(item.issued_at)}`,
			},
			{
				id: "number",
				name: `${t("lib:common.word.number")}`,
				type: "text",
				render: (item: PayoutInvoiceApi) => item.number && `${item.number}`,
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					NEW: t("enums.common.status.NEW"),
					CONFIRMED: t("enums.common.status.CONFIRMED"),
					DENIED: t("enums.common.status.DENIED"),
					PAID: t("enums.common.status.PAID"),
				},
				render: (item: PayoutInvoiceApi) => <FormatResourceStatus status={item.status} />,
				renderExport: (item: PayoutInvoiceApi) => t(`enums.common.status.${item.status}`),
			},
			{
				id: "total_price",
				name: `${t("common.word.total_price")}`,
				type: "number",
				render: (item: PayoutInvoiceApi) =>
					FormatMoney({
						amount: item?.total_price?.amount,
						currency: item?.total_price?.currency,
					}),
			},
		],
		selectedColumns: ["partner", "attachment", "created_at", "issued_at", "number", "status", "total_price"],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				click: (item: PayoutInvoiceApi) => {
					setShowUpdatePayoutInvoiceModal(item.id);
					setPayoutToShow(item);
				},
			},
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: (item: PayoutInvoiceApi) => onConfirmPayoutInvoice(item.id),
				visible: (item: PayoutInvoiceApi) => item.status === "NEW" || item.status === "DENIED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.reject", { ns: "lib" }),
				click: (item: PayoutInvoiceApi) => onRejectPayoutInvoice(item.id),
				visible: (item: PayoutInvoiceApi) => item.status === "NEW" || item.status === "CONFIRMED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.pay"),
				click: (item: PayoutInvoiceApi) => onPayPayoutInvoice(item.id),
				visible: (item: PayoutInvoiceApi) => item.status === "CONFIRMED",
				refreshListAfter: true,
			},
			{
				name: t("modules.partner_payout_invoice.action.cancel_approval.title"),
				click: (item: PayoutInvoiceApi) => onCancelConfirmForPayoutInvoice(item.id),
				visible: (item: PayoutInvoiceApi) => item.status === "CONFIRMED",
				refreshListAfter: true,
			},
			{
				name: t("modules.partner_payout_invoice.action.cancel_pay.title"),
				click: (item: PayoutInvoiceApi) => onCancelPaymentForPayoutInvoice(item.id),
				visible: (item: PayoutInvoiceApi) => item.status === "PAID",
				refreshListAfter: true,
			},
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params = {
				...params,
				include: "partner",
			};
			setParams(params);
			return adminApi.getPartnersInvoices(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.partner_payout_invoice.header.title"),
			filename: t("modules.partner_payout_invoice.field.export_config.filename"),
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.partner_payout_invoice.header.title")} />}
			<ListData
				data={items}
				config={config}
				onFetch={(items: PayoutInvoiceApi[]) => {
					setItems(items);
				}}
			/>
			{showUpdatePayoutInvoiceModal && (
				<UpdatePartnerPayoutInvoiceModalForm
					isShown={showUpdatePayoutInvoiceModal}
					payoutInvoice={payoutToShow}
					fetchPayoutInvoices={fetchPayoutInvoices}
					onHide={() => setShowUpdatePayoutInvoiceModal(null)}
				/>
			)}
		</div>
	);
};

const AdminPartnersPayoutInvoicesIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleChangeTabTitle(
			`${t("modules.partner_payout_invoice.header.title")} | ${t("lib:go_component.navigation.admin")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} />
		</Suspense>
	);
};

export default AdminPartnersPayoutInvoicesIndexPage;
